import api from './api'

export type MaturityStage = 'startup' | 'traction' | 'growth' | 'scale' | 'mature'

export interface Company {
  id: number
  name: string
  trade_name: string
  cnpj: string
  maturity_stage: MaturityStage
  pool_contribution_pct: number
  custom_min_mrr: string | null
  custom_min_margin: string | null
  effective_min_mrr: string
  effective_min_margin: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CreateCompanyRequest {
  name: string
  trade_name?: string
  cnpj?: string
  maturity_stage: MaturityStage
  is_active?: boolean
  custom_min_mrr?: string | null
  custom_min_margin?: string | null
}

export interface MonthlyEntry {
  id: number
  company: number
  company_name: string
  reference_date: string
  net_revenue: string
  net_margin: string
  created_at: string
  updated_at: string
}

export interface CreateMonthlyEntryRequest {
  reference_date: string
  net_revenue: string
  net_margin: string
}

export interface QuarterlyResult {
  id: number
  company: number
  company_name: string
  quarter: number
  year: number
  average_mrr: string
  average_margin: string
  is_eligible: boolean
  suspension_reason: string | null
  months_reported: number
  calculated_at: string
}

export interface GraduationStatus {
  qualifies: boolean
  current_stage: string
  next_stage: string | null
  consecutive_months: number
  required_months: number | null
  graduation_mrr: string | null
  graduation_nrr: string | null
}

export interface DowngradeStatus {
  qualifies: boolean
  current_stage: string
  previous_stage: string | null
  consecutive_mrr_below: number
  consecutive_margin_below: number
  required_months: number | null
  downgrade_mrr: string | null
  downgrade_margin: string | null
  reason: string | null
}

export interface Thresholds {
  mrr_min: string
  margin_min: string
  graduation_mrr: string | null
  graduation_months: string | null
  graduation_nrr: string | null
  downgrade_mrr: string | null
  downgrade_margin: string | null
  downgrade_months: string | null
}

export interface GroupOverviewCompany {
  company: Company
  thresholds: Thresholds
  quarterly_result: QuarterlyResult | null
  entries: MonthlyEntry[]
}

export interface GroupOverview {
  year: number
  quarter: number
  companies: GroupOverviewCompany[]
}

export interface CompanyDashboard {
  company: Company
  thresholds: Thresholds
  graduation: GraduationStatus
  downgrade: DowngradeStatus
  current_quarter_status: QuarterlyResult | null
  monthly_entries: MonthlyEntry[]
  quarterly_results: QuarterlyResult[]
}

export const companiesService = {
  // === Company CRUD ===

  async getAll(params?: {
    maturity_stage?: string
    is_active?: boolean
    search?: string
  }): Promise<Company[]> {
    const response = await api.get('/companies/', { params })
    const data = response.data
    return Array.isArray(data) ? data : data.results ?? []
  },

  async getById(id: number): Promise<Company> {
    const response = await api.get<Company>(`/companies/${id}/`)
    return response.data
  },

  async create(data: CreateCompanyRequest): Promise<Company> {
    const response = await api.post<Company>('/companies/', data)
    return response.data
  },

  async update(id: number, data: Partial<CreateCompanyRequest>): Promise<Company> {
    const response = await api.patch<Company>(`/companies/${id}/`, data)
    return response.data
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/companies/${id}/`)
  },

  // === Monthly Entries ===

  async getMonthlyEntries(companyId: number, params?: {
    year?: number
  }): Promise<MonthlyEntry[]> {
    const response = await api.get(`/companies/${companyId}/monthly-entries/`, { params })
    const data = response.data
    return Array.isArray(data) ? data : data.results ?? []
  },

  async createMonthlyEntry(companyId: number, data: CreateMonthlyEntryRequest): Promise<MonthlyEntry> {
    const response = await api.post<MonthlyEntry>(`/companies/${companyId}/monthly-entries/`, data)
    return response.data
  },

  async updateMonthlyEntry(companyId: number, entryId: number, data: Partial<CreateMonthlyEntryRequest>): Promise<MonthlyEntry> {
    const response = await api.patch<MonthlyEntry>(`/companies/${companyId}/monthly-entries/${entryId}/`, data)
    return response.data
  },

  async deleteMonthlyEntry(companyId: number, entryId: number): Promise<void> {
    await api.delete(`/companies/${companyId}/monthly-entries/${entryId}/`)
  },

  // === Quarterly Results ===

  async getQuarterlyResults(companyId: number, params?: {
    year?: number
  }): Promise<QuarterlyResult[]> {
    const response = await api.get(`/companies/${companyId}/quarterly-results/`, { params })
    const data = response.data
    return Array.isArray(data) ? data : data.results ?? []
  },

  // === Group Overview ===

  async getGroupOverview(year: number, quarter: number): Promise<GroupOverview> {
    const response = await api.get<GroupOverview>('/companies/group-overview/', {
      params: { year, quarter },
    })
    return response.data
  },

  // === Dashboard ===

  async getDashboard(companyId: number): Promise<CompanyDashboard> {
    const response = await api.get<CompanyDashboard>(`/companies/${companyId}/dashboard/`)
    return response.data
  },

  async recalculateQuarters(companyId: number): Promise<{ detail: string; recalculated: number; removed: number }> {
    const response = await api.post(`/companies/${companyId}/recalculate-quarters/`)
    return response.data
  },
}

export default companiesService
